'use client';

import { c as _c } from "react/compiler-runtime";
import './sac-controls.scss';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import type { RadioGroupProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import { TrackChildSearch, trackClick } from '@sola/tracking/components/datalayer.tsx';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { useSacQueryParams } from '../../utils/query-params.sac.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { buildChipItems, buildCountryOptions, buildDayOptions, buildGenderOptions, buildMonthOptions, buildTrackingEvent, getSerializedSacUrl, useSacControlsState } from './sac-controls.logic.ts';
type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  /** When true, reads filters from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
};

/* ─── Sub-components ─────────────────────────────────────────────────────────── */

interface SacControlsGridProps {
  className?: string;
  gender?: string;
  genderLabel: string;
  genderAriaLabel: string;
  genderOptions: RadioGroupProps['items'];
  onGenderChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  ageRange: [number, number];
  ageLabel: string;
  ageMin: number;
  ageMax: number;
  onAgeCommit: (payload: {
    newValue?: number[];
  }) => void;
  countryValue: string;
  countryLabel: string;
  countryOptions: SelectItemProps[];
  onCountryChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}
const SacControlsGrid: React.FC<SacControlsGridProps> = t0 => {
  const $ = _c(26);
  const {
    className,
    gender,
    genderLabel,
    genderAriaLabel,
    genderOptions,
    onGenderChange,
    ageRange,
    ageLabel,
    ageMin,
    ageMax,
    onAgeCommit,
    countryValue,
    countryLabel,
    countryOptions,
    onCountryChange
  } = t0;
  const t1 = `SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className ?? ""}`;
  let t2;
  if ($[0] !== genderLabel) {
    t2 = <label id="gender-group-label" className="form-label label">{genderLabel}</label>;
    $[0] = genderLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== gender || $[3] !== genderAriaLabel || $[4] !== genderOptions || $[5] !== onGenderChange) {
    t3 = <RadioGroup defaultValue={gender} aria-label={genderAriaLabel} loop={false} variant="buttons" name="gender" aria-labelledby="gender-group-label" onChange={onGenderChange} items={genderOptions} />;
    $[2] = gender;
    $[3] = genderAriaLabel;
    $[4] = genderOptions;
    $[5] = onGenderChange;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span>{t2}{t3}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== ageLabel || $[11] !== ageMax || $[12] !== ageMin || $[13] !== ageRange || $[14] !== onAgeCommit) {
    t5 = <span><SliderInput value={ageRange} label={ageLabel} min={ageMin} max={ageMax} name="age" showThumbLabels={true} onCommit={onAgeCommit} /></span>;
    $[10] = ageLabel;
    $[11] = ageMax;
    $[12] = ageMin;
    $[13] = ageRange;
    $[14] = onAgeCommit;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== countryLabel || $[17] !== countryOptions || $[18] !== countryValue || $[19] !== onCountryChange) {
    t6 = <span><Select value={countryValue} label={countryLabel} name="country" onChange={onCountryChange} items={countryOptions} /></span>;
    $[16] = countryLabel;
    $[17] = countryOptions;
    $[18] = countryValue;
    $[19] = onCountryChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t1 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className={t1}>{t4}{t5}{t6}</div>;
    $[21] = t1;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
interface BirthdayControlsProps {
  birthdayLabel: string;
  birthMonth?: number;
  birthMonthAriaLabel: string;
  monthOptions: SelectItemProps[];
  onBirthMonthChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
  birthDay?: number;
  birthDayAriaLabel: string;
  dayOptions: SelectItemProps[];
  onBirthDayChange: (v: React.ChangeEvent<HTMLSelectElement>) => void;
}

// TODO: add a reset-date button
const BirthdayControls: React.FC<BirthdayControlsProps> = t0 => {
  const $ = _c(19);
  const {
    birthdayLabel,
    birthMonth,
    birthMonthAriaLabel,
    monthOptions,
    onBirthMonthChange,
    birthDay,
    birthDayAriaLabel,
    dayOptions,
    onBirthDayChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArkIcon iconName="gift-01" className="SacControls__birthday-icon hidden md:block" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== birthdayLabel) {
    t2 = <div className="label-md label-moderate">{birthdayLabel}</div>;
    $[1] = birthdayLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== birthMonth || $[4] !== birthMonthAriaLabel || $[5] !== monthOptions || $[6] !== onBirthMonthChange) {
    t3 = <Select value={birthMonth} name="birth month" aria-label={birthMonthAriaLabel} onChange={onBirthMonthChange} items={monthOptions} />;
    $[3] = birthMonth;
    $[4] = birthMonthAriaLabel;
    $[5] = monthOptions;
    $[6] = onBirthMonthChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== birthDay || $[9] !== birthDayAriaLabel || $[10] !== dayOptions || $[11] !== onBirthDayChange) {
    t4 = <Select value={birthDay} name="birth day" aria-label={birthDayAriaLabel} onChange={onBirthDayChange} items={dayOptions} />;
    $[8] = birthDay;
    $[9] = birthDayAriaLabel;
    $[10] = dayOptions;
    $[11] = onBirthDayChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <span className="flex gap-comfy-sm">{t3}{t4}</span>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t5) {
    t6 = <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">{t1}{t2}{t5}</div>;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface AdvancedOptionsProps {
  showAdvanced: boolean;
  advancedLabel: string;
  onToggle: () => void;
  children: React.ReactNode;
}
const AdvancedOptions: React.FC<AdvancedOptionsProps> = t0 => {
  const $ = _c(14);
  const {
    showAdvanced,
    advancedLabel,
    onToggle,
    children
  } = t0;
  const t1 = showAdvanced && children;
  let t2;
  if ($[0] !== showAdvanced) {
    t2 = showAdvanced ? <ArkIcon iconName="chevron-up" /> : <ArkIcon iconName="chevron-down" />;
    $[0] = showAdvanced;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== advancedLabel || $[3] !== onToggle) {
    t3 = () => {
      trackClick(advancedLabel);
      onToggle();
    };
    $[2] = advancedLabel;
    $[3] = onToggle;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== advancedLabel || $[6] !== t3) {
    t4 = <button type="button" onClick={t3}>{advancedLabel}</button>;
    $[5] = advancedLabel;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <ButtonStyle variant="tertiary" fullWidth={true} iconAfter={t2}>{t4}</ButtonStyle>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className="SacControls__adv-search-container block md:hidden flex flex-col p-comfy-xs md:p-comfy-sm justify-center">{t1}{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};

/* ─── Main component ─────────────────────────────────────────────────────────── */

const InternalSacControls: React.FC<SacControlsProps & {
  sacFilters?: SacQueryParams;
  setSacFilters?: (values: Partial<SacQueryParams>) => void;
}> = t0 => {
  const $ = _c(95);
  let isDynamic;
  let props;
  let sacFilters;
  let setSacFilters;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      isDynamic,
      sacFilters,
      setSacFilters,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isDynamic;
    $[2] = props;
    $[3] = sacFilters;
    $[4] = setSacFilters;
    $[5] = t1;
  } else {
    isDynamic = $[1];
    props = $[2];
    sacFilters = $[3];
    setSacFilters = $[4];
    t1 = $[5];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["children", "core", "geo"];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    showAdvanced,
    ageRange,
    isMultipleCountry,
    sacMinAge,
    sacMaxAge,
    handleGenderChange,
    handleAgeSliderCommit,
    handleCountryChange,
    handleBirthMonthChange,
    handleBirthDayChange,
    handleChipChange,
    toggleAdvanced
  } = useSacControlsState(sacFilters, setSacFilters);
  let t3;
  if ($[7] !== t) {
    t3 = buildChipItems(t);
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const chips = t3;
  let t4;
  if ($[9] !== t) {
    t4 = buildGenderOptions(t);
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const genderOptions = t4;
  let t5;
  if ($[11] !== isMultipleCountry || $[12] !== t) {
    t5 = buildCountryOptions(t, isMultipleCountry);
    $[11] = isMultipleCountry;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const countryOptions = t5;
  let t6;
  if ($[14] !== t) {
    t6 = buildMonthOptions(t);
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const monthOptions = t6;
  let t7;
  if ($[16] !== t) {
    t7 = buildDayOptions(t);
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const dayOptions = t7;
  let t8;
  if ($[18] !== sacFilters) {
    t8 = buildTrackingEvent(sacFilters);
    $[18] = sacFilters;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const trackingEvent = t8;
  const showChips = variant !== "stacked";
  const t9 = sacFilters?.gender;
  let t10;
  if ($[20] !== t) {
    t10 = t("children:sac.wantToSponsor");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("children:sac.controls.chooseGender");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("children:sac.betweenTheAges");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = isMultipleCountry ? "multiple" : sacFilters?.countries?.[0] ?? "";
  let t14;
  if ($[26] !== t) {
    t14 = t("children:sac.whoLivesIn");
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== ageRange || $[29] !== countryOptions || $[30] !== genderOptions || $[31] !== handleAgeSliderCommit || $[32] !== handleCountryChange || $[33] !== handleGenderChange || $[34] !== sacMaxAge || $[35] !== sacMinAge || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t9) {
    t15 = {
      gender: t9,
      genderLabel: t10,
      genderAriaLabel: t11,
      genderOptions,
      onGenderChange: handleGenderChange,
      ageRange,
      ageLabel: t12,
      ageMin: sacMinAge,
      ageMax: sacMaxAge,
      onAgeCommit: handleAgeSliderCommit,
      countryValue: t13,
      countryLabel: t14,
      countryOptions,
      onCountryChange: handleCountryChange
    };
    $[28] = ageRange;
    $[29] = countryOptions;
    $[30] = genderOptions;
    $[31] = handleAgeSliderCommit;
    $[32] = handleCountryChange;
    $[33] = handleGenderChange;
    $[34] = sacMaxAge;
    $[35] = sacMinAge;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t9;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const gridProps = t15;
  let t16;
  if ($[43] !== t) {
    t16 = t("children:sac.birthdaySearch");
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = sacFilters?.birthMonth;
  let t18;
  if ($[45] !== t) {
    t18 = t("children:sac.controls.month");
    $[45] = t;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const t19 = sacFilters?.birthDay;
  let t20;
  if ($[47] !== t) {
    t20 = t("children:sac.controls.day");
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== dayOptions || $[50] !== handleBirthDayChange || $[51] !== handleBirthMonthChange || $[52] !== monthOptions || $[53] !== t16 || $[54] !== t17 || $[55] !== t18 || $[56] !== t19 || $[57] !== t20) {
    t21 = {
      birthdayLabel: t16,
      birthMonth: t17,
      birthMonthAriaLabel: t18,
      monthOptions,
      onBirthMonthChange: handleBirthMonthChange,
      birthDay: t19,
      birthDayAriaLabel: t20,
      dayOptions,
      onBirthDayChange: handleBirthDayChange
    };
    $[49] = dayOptions;
    $[50] = handleBirthDayChange;
    $[51] = handleBirthMonthChange;
    $[52] = monthOptions;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  const birthdayProps = t21;
  const t22 = `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`;
  let t23;
  if ($[59] !== t22) {
    t23 = cn(t22);
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t) {
    t24 = t("children:sac.controls.legend");
    $[61] = t;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== trackingEvent) {
    t25 = <TrackChildSearch event={trackingEvent} />;
    $[63] = trackingEvent;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== birthdayProps || $[66] !== gridProps || $[67] !== showAdvanced || $[68] !== t || $[69] !== toggleAdvanced || $[70] !== variant) {
    t26 = variant !== "stacked" && <><SacControlsGrid {...gridProps} className="hidden md:grid" /><BirthdayControls {...birthdayProps} /><AdvancedOptions showAdvanced={showAdvanced} advancedLabel={t("children:sac.advancedSearch")} onToggle={toggleAdvanced}><SacControlsGrid {...gridProps} /></AdvancedOptions></>;
    $[65] = birthdayProps;
    $[66] = gridProps;
    $[67] = showAdvanced;
    $[68] = t;
    $[69] = toggleAdvanced;
    $[70] = variant;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== gridProps || $[73] !== props || $[74] !== variant) {
    t27 = variant === "stacked" && <><FeatureBase {...props} {...getBaseSizeProps(2)} {...{
        primaryCTA: undefined,
        secondaryCTA: undefined,
        tertiaryCTA: undefined
      }} className="p-comfy-xl pb-none" inline={true} /><SacControlsGrid {...gridProps} className="flex flex-col" /></>;
    $[72] = gridProps;
    $[73] = props;
    $[74] = variant;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== isDynamic || $[77] !== sacFilters || $[78] !== t) {
    t28 = !isDynamic && <div className="p-comfy-xl"><ButtonStyle variant="primary"><Link href={getSerializedSacUrl(sacFilters)}>{t("core:actions.sponsorAChild")}</Link></ButtonStyle></div>;
    $[76] = isDynamic;
    $[77] = sacFilters;
    $[78] = t;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== t26 || $[81] !== t27 || $[82] !== t28) {
    t29 = <div className="SacControls__card flex flex-col">{t26}{t27}{t28}</div>;
    $[80] = t26;
    $[81] = t27;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== chips || $[85] !== handleChipChange || $[86] !== sacFilters?.filters || $[87] !== showChips) {
    t30 = showChips && <ChipGroup chips={chips} selectedValues={sacFilters?.filters} handleSelectionChange={handleChipChange} isScrollable={true} />;
    $[84] = chips;
    $[85] = handleChipChange;
    $[86] = sacFilters?.filters;
    $[87] = showChips;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  let t31;
  if ($[89] !== t23 || $[90] !== t24 || $[91] !== t25 || $[92] !== t29 || $[93] !== t30) {
    t31 = <div className={t23} aria-label={t24} role="group">{t25}{t29}{t30}</div>;
    $[89] = t23;
    $[90] = t24;
    $[91] = t25;
    $[92] = t29;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  return t31;
};
export const LiveSacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(17);
  const {
    isDynamic,
    gender,
    countries,
    ageMin,
    ageMax,
    disability,
    hiv,
    orphan,
    highlyVulnerable
  } = props;
  const hasDefaults = isDynamic && (gender !== undefined || countries !== undefined || ageMin !== undefined || ageMax !== undefined || disability !== undefined || hiv !== undefined || orphan !== undefined || highlyVulnerable !== undefined);
  let t0;
  if ($[0] !== ageMax || $[1] !== ageMin || $[2] !== countries || $[3] !== disability || $[4] !== gender || $[5] !== hasDefaults || $[6] !== highlyVulnerable || $[7] !== hiv || $[8] !== orphan) {
    t0 = hasDefaults ? {
      gender: gender as ChildGenderEnum | undefined,
      countries,
      ageMin,
      ageMax,
      disability,
      hiv,
      orphan,
      highlyVulnerable
    } : undefined;
    $[0] = ageMax;
    $[1] = ageMin;
    $[2] = countries;
    $[3] = disability;
    $[4] = gender;
    $[5] = hasDefaults;
    $[6] = highlyVulnerable;
    $[7] = hiv;
    $[8] = orphan;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== isDynamic || $[11] !== t0) {
    t1 = {
      isDynamic,
      defaults: t0
    };
    $[10] = isDynamic;
    $[11] = t0;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const {
    sacFilters,
    setSacFilters
  } = useSacQueryParams(t1);
  let t2;
  if ($[13] !== props || $[14] !== sacFilters || $[15] !== setSacFilters) {
    t2 = <InternalSacControls {...props} sacFilters={sacFilters} setSacFilters={setSacFilters} />;
    $[13] = props;
    $[14] = sacFilters;
    $[15] = setSacFilters;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
};

/** SSG fallback renders without search params; Suspense swaps in the live version. */
export const SacControls: React.FC<SacControlsProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<InternalSacControls {...props} />}><LiveSacControls {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};